import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ScrollReveal from '../components/ScrollReveal';
import { fetchCollection, getVal } from '../lib/api';

export default function Lejebetingelser() {
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    async function loadTerms() {
      try {
        const res = await fetchCollection('sider', { term: 'sidetype:lejebetingelser' });
        if (res.items && res.items.length > 0) {
          setPageData(res.items[0]);
        }
      } catch (err) {
        console.error('Fejl ved hentning af lejebetingelser fra CMS:', err);
      }
    }
    loadTerms();
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-stone-50">
      {/* Hero Header */}
      <section className="border-b border-stone-200 bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <ScrollReveal animation="fade-in-down">
            <span className="text-xs font-bold uppercase tracking-[0.35em] text-brand-orange">Sikkerhed & Tryghed</span>
            <h1 className="mt-3 text-4xl font-semibold text-slate-800 sm:text-5xl">
              {getVal(pageData?.title) || 'Lejebetingelser'}
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-slate-500 sm:text-base">
              {getVal(pageData?.subtitle) || 'Her finder du vores gældende betingelser for leje af maskiner, telte, hoppeborge og festudstyr hos J F D Service.'}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          
          {/* Fællesbetingelser Section */}
          <ScrollReveal animation="fade-in" className="mb-16">
            <div className="rounded-2xl border border-stone-200 bg-white p-8 shadow-sm sm:p-12">
              <div className="flex items-center gap-4 border-b border-stone-100 pb-6 mb-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange">
                  <i className="fa-solid fa-file-signature text-xl"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">Fællesbetingelser</h2>
                  <p className="text-xs text-slate-400 mt-0.5">Gælder for alle lejemål og produkter</p>
                </div>
              </div>

              <div className="space-y-8">
                {/* Lejeperiode */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="flex items-start gap-3 md:col-span-1">
                    <div className="mt-1 text-brand-orange">
                      <i className="fa-solid fa-calendar-days"></i>
                    </div>
                    <h3 className="font-semibold text-slate-800">Lejeperiode</h3>
                  </div>
                  <div className="md:col-span-2 text-sm leading-relaxed text-slate-600 space-y-2">
                    <p>
                      Såfremt andet ikke er aftalt, og skriftligt bekræftet, starter Lejemålet <strong className="text-slate-800">kl. 11.00 på lejedagen</strong> og slutter <strong className="text-slate-800">kl. 11.00 den følgende dag</strong>.
                    </p>
                    <p className="text-xs text-slate-400">
                      Fremleje er ikke tilladt uden udlejers skriftlige samtykke.
                    </p>
                  </div>
                </div>

                <hr className="border-stone-100" />

                {/* Fejl og mangler */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="flex items-start gap-3 md:col-span-1">
                    <div className="mt-1 text-brand-orange">
                      <i className="fa-solid fa-clipboard-check"></i>
                    </div>
                    <h3 className="font-semibold text-slate-800">Fejl & mangler</h3>
                  </div>
                  <div className="md:col-span-2 text-sm leading-relaxed text-slate-600">
                    <p>
                      Ved afhentning hhv. levering gennemgås udstyret, og evt. fejl meddeles straks til udlejer. Hvis kunden ikke gør opmærksom på fejl eller mangler ved udstyret, betragtes dette som i fuld funktionel og fejlfri stand. Det lejede skal altid leveres tilbage i samme stand som det modtages.
                    </p>
                    <p className="mt-2 text-xs font-medium text-brand-sky">
                      <i className="fa-solid fa-circle-info mr-1"></i> Manglende eller beskadigede genstande afregnes efter afleveringen.
                    </p>
                  </div>
                </div>

                <hr className="border-stone-100" />

                {/* Lejers ansvar */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="flex items-start gap-3 md:col-span-1">
                    <div className="mt-1 text-brand-orange">
                      <i className="fa-solid fa-shield-halved"></i>
                    </div>
                    <h3 className="font-semibold text-slate-800">Lejers ansvar</h3>
                  </div>
                  <div className="md:col-span-2 text-sm leading-relaxed text-slate-600">
                    <p>
                      Lejer står til ansvar for alt hærværk, tyveri, samt overbelastning af det lejede.
                    </p>
                  </div>
                </div>

                <hr className="border-stone-100" />

                {/* Lejer henter selv */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="flex items-start gap-3 md:col-span-1">
                    <div className="mt-1 text-brand-orange">
                      <i className="fa-solid fa-trailer"></i>
                    </div>
                    <h3 className="font-semibold text-slate-800">Selvafhentning</h3>
                  </div>
                  <div className="md:col-span-2 text-sm leading-relaxed text-slate-600 space-y-3">
                    <p>
                      Afhentning og returnering ved lejer skal ske i lukket vogn (trailer eller bil), og er for lejers egen risiko. Lejer står selv for af- og pålæsning, og er ansvarlig for at medbringe grej til fastgørelse i bil/trailer.
                    </p>
                    <div className="rounded-lg bg-stone-50 p-4 text-xs text-slate-500 border border-stone-100">
                      <strong className="text-slate-700 block mb-1">Fleksibel afhentning:</strong>
                      Lejemålet starter kl. 11.00 og slutter den efterfølgende dag kl. 11.00 – såfremt udstyret ikke er udlejet hhv. dagen før og/eller dagen efter, kan det i hvert enkelt tilfælde aftales, at afhentning kan ske aftenen før og/eller returnering kan ske senere end kl. 11.00.
                    </div>
                  </div>
                </div>

                <hr className="border-stone-100" />

                {/* Udlejer leverer og afhenter */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="flex items-start gap-3 md:col-span-1">
                    <div className="mt-1 text-brand-orange">
                      <i className="fa-solid fa-truck-ramp-box"></i>
                    </div>
                    <h3 className="font-semibold text-slate-800">Levering & afhentning</h3>
                  </div>
                  <div className="md:col-span-2 text-sm leading-relaxed text-slate-600 space-y-2">
                    <p>
                      Såfremt vi skal levere og afhente, skal det sikres, at der kan køres i bil frem til det ønskede aflæsningssted.
                    </p>
                    <p>
                      De lejede effekter kan, hvis der kan køres med palleløfter / sækkevogn af udlejer transporteres frem til brugsstedet, dog <strong className="text-slate-800">max. 50 meter</strong> i samme niveau som der kan køres til.
                    </p>
                    <p className="text-xs text-amber-600 font-medium">
                      <i className="fa-solid fa-triangle-exclamation mr-1"></i> Der leveres ikke til etagebyggeri med mindre der er elevator, og vejen dertil kan klares uden løft og trapper.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* Specielt for Hoppeborge Section */}
          <ScrollReveal animation="fade-in">
            <div className="rounded-2xl border border-stone-200 bg-white p-8 shadow-sm sm:p-12">
              <div className="flex items-center gap-4 border-b border-stone-100 pb-6 mb-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-sky/10 text-brand-sky">
                  <i className="fa-solid fa-circle-exclamation text-xl"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">Specielt for Hoppeborge</h2>
                  <p className="text-xs text-slate-400 mt-0.5">Sikkerhedsregler og lovgivning</p>
                </div>
              </div>

              <div className="space-y-8">
                {/* Lejers ansvar under brug */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="flex items-start gap-3 md:col-span-1">
                    <div className="mt-1 text-brand-sky">
                      <i className="fa-solid fa-user-shield"></i>
                    </div>
                    <h3 className="font-semibold text-slate-800">Ansvar under brug</h3>
                  </div>
                  <div className="md:col-span-2 text-sm leading-relaxed text-slate-600 space-y-3">
                    <p>
                      Lejer står til ansvar for al hærværk, tyveri, samt overbelastning af det lejede.
                    </p>
                    <div className="rounded-xl border border-red-100 bg-red-50/50 p-5">
                      <span className="font-semibold text-red-800 text-xs uppercase tracking-wider block mb-3">
                        Under brug SKAL lejer sørge for at:
                      </span>
                      <ul className="space-y-2 text-xs text-slate-600">
                        <li className="flex items-start gap-2">
                          <i className="fa-solid fa-circle-check text-green-600 mt-0.5 shrink-0"></i>
                          <span>Der skal <strong>ALTID</strong> være en ansvarlig voksen (over 18 år), der holder opsyn og styrer hoppeborgen.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="fa-solid fa-circle-xmark text-red-500 mt-0.5 shrink-0"></i>
                          <span>Det er strengt forbudt at bære fodtøj i hoppeborgen.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="fa-solid fa-circle-xmark text-red-500 mt-0.5 shrink-0"></i>
                          <span>Det er strengt forbudt at klatre og hænge på siderne.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="fa-solid fa-circle-xmark text-red-500 mt-0.5 shrink-0"></i>
                          <span>Det er strengt forbudt at sidde på puden foran hoppeborgen.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="fa-solid fa-circle-xmark text-red-500 mt-0.5 shrink-0"></i>
                          <span>Det er strengt forbudt at medbringe slik, mad, drikkevarer eller legetøj i hoppeborgen.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="fa-solid fa-circle-check text-green-600 mt-0.5 shrink-0"></i>
                          <span>Hoppeborgen må max. benyttes af <strong>11 personer</strong> ad gangen.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="fa-solid fa-circle-xmark text-red-500 mt-0.5 shrink-0"></i>
                          <span>Tagfat og andre vilde lege bør ikke accepteres i borgen.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="fa-solid fa-cloud-showers-water text-amber-600 mt-0.5 shrink-0"></i>
                          <span>Ved stærk blæst og regn skal aktiviteten indstilles af hensyn til sikkerheden.</span>
                        </li>
                      </ul>
                    </div>
                    <p className="text-xs text-slate-400 italic">
                      Brugen af forlystelsen sker på eget ansvar.
                    </p>
                  </div>
                </div>

                <hr className="border-stone-100" />

                {/* Forholdsregler inden opstilling */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="flex items-start gap-3 md:col-span-1">
                    <div className="mt-1 text-brand-sky">
                      <i className="fa-solid fa-compass-drafting"></i>
                    </div>
                    <h3 className="font-semibold text-slate-800">Inden opstilling</h3>
                  </div>
                  <div className="md:col-span-2 text-sm leading-relaxed text-slate-600 space-y-2">
                    <p>
                      Det påhviler lejer at sørge for, at pladsen, hvor hoppeborgen skal opstilles, er plan og ryddet for eventuelle skarpe genstande og dyreekskrementer før opstillingen.
                    </p>
                    <p>
                      Hoppeborgen skal altid opstilles på det medfølgende underlag, som skal fastgøres til jorden med de medfølgende pløkke, ligesom selve borgen skal fastgøres vha. de medfølgende barduner og pløkke.
                    </p>
                    <p className="text-xs text-brand-orange font-semibold">
                      <i className="fa-solid fa-circle-info mr-1"></i> Lejer skal altid følge den af udlejer udleverede vejledning.
                    </p>
                  </div>
                </div>

                <hr className="border-stone-100" />

                {/* Godkendelse fra kommunen */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="flex items-start gap-3 md:col-span-1">
                    <div className="mt-1 text-brand-sky">
                      <i className="fa-solid fa-building-shield"></i>
                    </div>
                    <h3 className="font-semibold text-slate-800">Godkendelse</h3>
                  </div>
                  <div className="md:col-span-2 text-sm leading-relaxed text-slate-600 space-y-2">
                    <p>
                      Afvikles arrangementet udendørs på kommunens område (fx grønne områder, torvet, havnepladsen etc.), så skal man have en tilladelse fra kommunen, der kan vedhæftes ansøgningen til politiet.
                    </p>
                    <p>
                      Såfremt arrangementet afvikles på privat grund, skal der ikke foreligge en godkendelse fra kommunen – medmindre der kan være tale om, at arrangementet påvirker den lokale trafik eller lignende.
                    </p>
                  </div>
                </div>

                <hr className="border-stone-100" />

                {/* Lovgivning om offentlige forlystelser */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="flex items-start gap-3 md:col-span-1">
                    <div className="mt-1 text-brand-sky">
                      <i className="fa-solid fa-gavel"></i>
                    </div>
                    <h3 className="font-semibold text-slate-800">Lovgivning</h3>
                  </div>
                  <div className="md:col-span-2 text-sm leading-relaxed text-slate-600 space-y-3">
                    <p>
                      Når du opstiller en hoppeborg, aktivitetsbane, rodeotyr eller lignende aktivitet, skal du være opmærksom på, at der skal leves op til disse krav:
                    </p>
                    <ul className="space-y-2 text-xs text-slate-500 list-disc list-inside pl-1">
                      <li>Det må ikke blæse mere end 9 meter i sekundet / ved 10 meter i sekundet skal hoppeborgen lukkes ned.</li>
                      <li>Hoppeborge må ikke bruges i regnvejr.</li>
                      <li>Der skal være god plads rundt om hoppeborgen, hvilket står nærmere beskrevet i synsbogen.</li>
                      <li>Hoppeborgen skal betjenes/være under opsyn af en person over 18 år, der er sat ind i at betjene hoppeborgen.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* Contact CTA */}
          <ScrollReveal animation="fade-in-up" className="mt-16 text-center">
            <div className="rounded-2xl bg-slate-900 p-8 text-white shadow-md sm:p-12">
              <h3 className="text-2xl font-semibold sm:text-3xl">Har du spørgsmål til betingelserne?</h3>
              <p className="mx-auto mt-3 max-w-xl text-sm text-slate-300">
                Vi sidder klar til at besvare dine spørgsmål, så du kan føle dig helt tryg ved dit lejemål.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <Link
                  to="/kontakt"
                  className="rounded-xl bg-brand-orange px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-orange-hover"
                >
                  Kontakt os nu
                </Link>
                <Link
                  to="/priser"
                  className="rounded-xl border border-slate-700 bg-slate-800 px-6 py-3 text-sm font-semibold text-slate-200 transition-colors hover:bg-slate-700"
                >
                  Se priser
                </Link>
              </div>
            </div>
          </ScrollReveal>

        </div>
      </section>
    </div>
  );
}
